import React from 'react';

import {assert} from 'Asserts';
import * as contextmenu from 'components/ContextMenu';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  concentrationUnitTypePredicate,
  containerQuantityUnitTypePredicate,
} from 'inventory/InventoryUnitUtil';
import {UnitsColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  ConcentrationStructuredTableColumn,
  QuantityStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {ColumnConstraint, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import TableAxisCell, {TableAxisCellProps} from 'notebook/tables/core-ui/TableAxisCell';
import {useAxisCellModel} from 'notebook/tables/store/hooks/useAxisCellModel';
import {isColumnHeaderModel} from 'notebook/tables/store/models/AxisCellModel';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import {UNIT_TYPES_QUERY} from 'units/UnitsConstants';

export interface UnitsColumnConstraint extends ColumnConstraint {
  columnDefinition: UnitsColumnDefinition;
  structuredTableColumn: ConcentrationStructuredTableColumn | QuantityStructuredTableColumn;
}

function isUnitsColumnConstraint(
  columnConstraint: ColumnConstraint
): columnConstraint is UnitsColumnConstraint {
  return (
    columnConstraint.columnDefinition?.type === TableColumnType.QUANTITY ||
    columnConstraint.columnDefinition?.type === TableColumnType.CONCENTRATION
  );
}

function getUpdatedColumnConstraint(
  columnConstraint: UnitsColumnConstraint,
  newUnit: {symbol: string; id?: string}
): UnitsColumnConstraint {
  return {
    ...columnConstraint,
    columnDefinition: {
      ...columnConstraint.columnDefinition,
      unitApiIdentifier: newUnit.id,
      units: newUnit.symbol,
    },
    structuredTableColumn: {
      ...columnConstraint.structuredTableColumn,
      unitApiIdentifier: newUnit.id,
      units: newUnit.symbol,
    },
  };
}

type ClickHandler = (newUnit: {symbol: string; id?: string}) => void;

// For usability we want to ensure that certain common unit types are listed first
const ordering = ['Molar concentration', 'Mass concentration', 'Volume', 'Mass', 'Amount of substance'];
const compareUnitTypes = (a: string, b: string): number =>
  (ordering.indexOf(a) < 0 ? 5 : ordering.indexOf(a)) - (ordering.indexOf(b) < 0 ? 5 : ordering.indexOf(b));

function makeUnitDictionaryMenuOptions(
  allUnitTypes: UnitTypesQuery['unitTypes'],
  columnType: TableColumnType,
  onClick: ClickHandler
): Array<contextmenu.ContextMenuOption> {
  const filterPredicate =
    columnType === TableColumnType.CONCENTRATION
      ? concentrationUnitTypePredicate
      : containerQuantityUnitTypePredicate;
  const unitTypes = allUnitTypes.filter((ut) => filterPredicate(ut) && !ut.archiveData.archived);

  const options: Array<contextmenu.ContextMenuOption> = [];
  unitTypes
    .sort((a, b) => compareUnitTypes(a.name, b.name))
    .forEach((ut) => {
      options.push({
        name: ut.name,
        disabled: true,
      });
      ut.units
        .filter((u) => !u.archiveData.archived)
        .forEach((u) => {
          options.push({
            name: u.symbol,
            id: u.id,
            click: () => {
              onClick({symbol: u.symbol, id: u.id});
            },
          });
        });
    });
  return options;
}

function UnitsTableAxisCell(props: TableAxisCellProps): JSX.Element {
  const columnIndex = useAxisCellModel((axisCellModel) => axisCellModel.index);
  const columnConstraint = useAxisCellModel((axisCellModel) =>
    isColumnHeaderModel(axisCellModel) ? axisCellModel.columnConstraint : null
  );
  const isLocked = useAxisCellModel((axisCellModel) => axisCellModel.isLocked);
  const {setColumnConstraint} = useAxisCellModel((axisCellModel) => axisCellModel.mutations);
  const unitTypesResponse = useSkippableBenchlingQuery<UnitTypesQuery>(UNIT_TYPES_QUERY, {
    variables: {},
  });

  if (!isUnitsColumnConstraint(columnConstraint!)) {
    assert(false, 'UnitsTableAxisCell called without a UnitsColumnConstraint');
  }
  const onClick = (newUnit: {symbol: string; id?: string}): void => {
    setColumnConstraint(columnIndex, getUpdatedColumnConstraint(columnConstraint, newUnit), {});
  };

  const columnDefinition = columnConstraint.columnDefinition;
  const showMenu = !columnDefinition?.hideContextMenu && !isLocked;

  let unitsMenuOptions: Array<contextmenu.ContextMenuOption> = [];
  if (showMenu && columnDefinition && unitTypesResponse.type === QueryResultType.SUCCESS) {
    unitsMenuOptions = makeUnitDictionaryMenuOptions(
      unitTypesResponse.data.unitTypes,
      columnDefinition.type,
      onClick
    );
  }

  // The unit list is fetched rather than derived from the table, so it is contributed by this
  // component rather than by the table's own extra context menu options.
  const wrappedOptions = unitsMenuOptions.length ? [{name: 'Units', menu: unitsMenuOptions}] : [];

  return <TableAxisCell {...props} additionalContextMenuOptions={wrappedOptions} />;
}

export default UnitsTableAxisCell;
